import React, { useEffect, useState } from 'react';
import { BatteryCharging, X, CheckCircle2, AlertTriangle, ExternalLink, Power } from 'lucide-react';
import {
  detectVendor,
  openAppDetails,
  openAutostart,
  openBatteryOptIgnore,
} from '../../lib/batterySettings';

const STORAGE_KEY = 'battery_wizard_done_v1';

/**
 * BatterySettingsWizard
 *
 * One-shot guided modal that helps staff disable battery optimisation +
 * enable auto-start for Carloo Tex on aggressive OEM ROMs (MIUI / ColorOS /
 * FuntouchOS / OxygenOS / OneUI). Three numbered steps:
 *
 *   1. Disable battery optimisation         (standard Android settings page)
 *   2. Allow auto-start                      (vendor-specific page)
 *   3. Pin the foreground notification       (informational only)
 *
 * Staff taps "All done" when finished — flag is persisted so the wizard
 * never auto-pops again. Still accessible from the dashboard icon.
 *
 * Props:
 *   • open   : boolean
 *   • onClose: () => void
 */
export default function BatterySettingsWizard({ open, onClose }) {
  const [vendor, setVendor] = useState('android');
  const [step, setStep] = useState(1);

  useEffect(() => {
    if (!open) return;
    setVendor(detectVendor());
    setStep(1);
  }, [open]);

  if (!open) return null;

  const isVendorSpecific = ['xiaomi', 'oppo', 'realme', 'vivo', 'oneplus', 'samsung', 'honor'].includes(vendor);

  const handleFinish = () => {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch (_) {}
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="battery-wizard">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl max-h-[92vh] flex flex-col">
        <div className="bg-gradient-to-br from-amber-500 to-orange-600 text-white px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BatteryCharging size={22} />
            <div>
              <h2 className="font-bold text-base">Optimise Battery Settings</h2>
              <p className="text-white/80 text-[11px]">Step {step} of 3 · for {labelOfVendor(vendor)}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full" data-testid="battery-wizard-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto" data-testid={`battery-wizard-step-${step}`}>
          {step === 1 && (
            <Step1
              onNext={() => setStep(2)}
              onOpenSettings={openBatteryOptIgnore}
            />
          )}
          {step === 2 && (
            <Step2
              vendor={vendor}
              isVendorSpecific={isVendorSpecific}
              onBack={() => setStep(1)}
              onNext={() => setStep(3)}
              onOpenAutostart={() => openAutostart(vendor)}
              onOpenAppDetails={openAppDetails}
            />
          )}
          {step === 3 && (
            <Step3
              onBack={() => setStep(2)}
              onFinish={handleFinish}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function labelOfVendor(v) {
  return ({
    xiaomi: 'Xiaomi / Redmi / Poco', oppo: 'Oppo', realme: 'Realme',
    vivo: 'Vivo', oneplus: 'OnePlus', samsung: 'Samsung', honor: 'Honor / Huawei',
    android: 'Android device', other: 'this device',
  })[v] || 'Android device';
}

function Step1({ onNext, onOpenSettings }) {
  return (
    <>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex gap-2">
        <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800">
          Your phone may pause Carloo Tex when the screen is off, causing GPS gaps in your visit trail. This is fixable in 60 seconds.
        </p>
      </div>

      <h3 className="font-bold text-gray-900 mb-1">1️⃣ Disable battery optimisation</h3>
      <ol className="list-decimal pl-5 text-sm text-gray-700 space-y-1 mb-4">
        <li>Tap the button below — it opens the <strong>Battery optimisation</strong> page.</li>
        <li>Find <strong>Carloo Tex</strong> in the list (you may need to switch from "Not optimised" → "All apps").</li>
        <li>Tap it → choose <strong>Don't optimise</strong> / <strong>Allow</strong>.</li>
        <li>Come back here.</li>
      </ol>

      <button
        onClick={onOpenSettings}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold mb-2"
        data-testid="battery-step1-open"
      >
        <Power size={16} /> Open battery settings <ExternalLink size={13} />
      </button>
      <button
        onClick={onNext}
        className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-xl"
        data-testid="battery-step1-next"
      >
        Done · Continue to step 2 →
      </button>
    </>
  );
}

function Step2({ vendor, isVendorSpecific, onBack, onNext, onOpenAutostart, onOpenAppDetails }) {
  return (
    <>
      <h3 className="font-bold text-gray-900 mb-1">2️⃣ Allow auto-start</h3>
      {isVendorSpecific ? (
        <>
          <p className="text-sm text-gray-700 mb-3">
            {labelOfVendor(vendor)} phones have an <strong>extra autostart list</strong> apart from Android battery optimisation. We need to whitelist Carloo Tex there too.
          </p>
          <div className="bg-gray-50 border rounded-xl p-3 mb-4 text-sm text-gray-700">
            {vendorSpecificSteps[vendor]}
          </div>
          <button
            onClick={onOpenAutostart}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold mb-2"
            data-testid="battery-step2-open"
          >
            Open {labelOfVendor(vendor)} autostart <ExternalLink size={13} />
          </button>
        </>
      ) : (
        <>
          <p className="text-sm text-gray-700 mb-3">
            Your phone doesn't have a vendor-specific autostart list — the Android battery setting in step&nbsp;1 is enough.
          </p>
          <button
            onClick={onOpenAppDetails}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm mb-2"
            data-testid="battery-step2-open"
          >
            (Optional) Open App Info <ExternalLink size={13} />
          </button>
        </>
      )}

      <div className="flex gap-2">
        <button
          onClick={onBack}
          className="flex-1 px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-xl"
          data-testid="battery-step2-back"
        >← Back</button>
        <button
          onClick={onNext}
          className="flex-1 px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold"
          data-testid="battery-step2-next"
        >Done · Continue →</button>
      </div>
    </>
  );
}

function Step3({ onBack, onFinish }) {
  return (
    <>
      <h3 className="font-bold text-gray-900 mb-1">3️⃣ Keep the GPS notification visible</h3>
      <p className="text-sm text-gray-700 mb-3">
        While you're checked in, a small notification will say <em>"Tracking active. Your visits and breaks are being recorded."</em>
      </p>
      <ul className="text-sm text-gray-700 space-y-2 mb-4">
        <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-emerald-600 mt-0.5" /> <span><strong>Don't swipe this notification away</strong> — it's what keeps GPS running.</span></li>
        <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-emerald-600 mt-0.5" /> Your phone can sleep / auto-lock normally. GPS still works.</li>
        <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-emerald-600 mt-0.5" /> You only need to do steps 1 &amp; 2 <strong>once</strong>.</li>
      </ul>

      {/* Feb 22, 2026 — VoLTE hint. Older 3G-only calls cut off mobile data
          for the call's duration, so GPS pings can't reach the server until
          the call ends. On 4G/5G with VoLTE enabled, data + voice run in
          parallel and tracking stays live. */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-sm text-amber-900">
        <div className="font-semibold mb-1 flex items-center gap-1">📶 Bonus tip — Enable VoLTE (HD Voice)</div>
        <p className="text-xs leading-relaxed">
          If your phone drops mobile data while you're on a call, GPS tracking pauses until you hang up. Enable <strong>VoLTE</strong> (also called <strong>HD Voice</strong> or <strong>4G Calling</strong>) in your SIM/Mobile Network settings so voice + data run at the same time.
        </p>
      </div>

      <div className="flex gap-2">
        <button
          onClick={onBack}
          className="flex-1 px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-xl"
          data-testid="battery-step3-back"
        >← Back</button>
        <button
          onClick={onFinish}
          className="flex-1 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold"
          data-testid="battery-step3-finish"
        >✓ All done</button>
      </div>
    </>
  );
}

const vendorSpecificSteps = {
  xiaomi: (
    <ol className="list-decimal pl-5 space-y-1">
      <li>Tap "Open Xiaomi autostart" below.</li>
      <li>Find <strong>Carloo Tex</strong> → toggle <strong>ON</strong>.</li>
      <li>Also: Settings → Apps → Carloo Tex → Battery saver → <strong>No restrictions</strong>.</li>
    </ol>
  ),
  oppo: (
    <ol className="list-decimal pl-5 space-y-1">
      <li>Tap "Open Oppo autostart" below.</li>
      <li>Find <strong>Carloo Tex</strong> → toggle <strong>Allow auto-launch</strong> ON.</li>
      <li>Long-press Carloo Tex in Recent Apps → tap the <strong>🔒</strong> lock icon (prevents kill).</li>
    </ol>
  ),
  realme: (
    <ol className="list-decimal pl-5 space-y-1">
      <li>Tap "Open Realme autostart" below.</li>
      <li>Find <strong>Carloo Tex</strong> → toggle <strong>Auto-launch</strong> ON.</li>
      <li>Settings → Battery → App battery management → <strong>Carloo Tex → Allow background activity</strong>.</li>
    </ol>
  ),
  vivo: (
    <ol className="list-decimal pl-5 space-y-1">
      <li>Tap "Open Vivo autostart" below.</li>
      <li>Find <strong>Carloo Tex</strong> → toggle <strong>Background high power consumption</strong> ON.</li>
      <li>Settings → Battery → High background power consumption → Carloo Tex.</li>
    </ol>
  ),
  oneplus: (
    <ol className="list-decimal pl-5 space-y-1">
      <li>Tap "Open OnePlus chain-launch" below.</li>
      <li>Find <strong>Carloo Tex</strong> → toggle ON.</li>
      <li>Settings → Battery → Battery optimisation → Carloo Tex → <strong>Don't optimise</strong>.</li>
    </ol>
  ),
  samsung: (
    <ol className="list-decimal pl-5 space-y-1">
      <li>Tap "Open Samsung battery" below.</li>
      <li>Background usage limits → <strong>Never sleeping apps</strong> → Add <strong>Carloo Tex</strong>.</li>
      <li>Make sure Carloo Tex is NOT in "Deep sleeping apps".</li>
    </ol>
  ),
  honor: (
    <ol className="list-decimal pl-5 space-y-1">
      <li>Tap "Open Honor protected apps" below.</li>
      <li>Find <strong>Carloo Tex</strong> → toggle ON.</li>
      <li>Settings → Battery → Launch → Carloo Tex → enable all three toggles.</li>
    </ol>
  ),
};

export const BATTERY_WIZARD_STORAGE_KEY = STORAGE_KEY;
